import { useEffect, useCallback, useRef } from "react";
import { useAppDispatch } from "./store";
import { pingFrameWithCurrentState } from "./actions/sharedActions";
import { useFramePubSub } from "./pubsub";

// NOTE(liamvdv): The first implementation of this hook used the Page Visibility API.
// Both approaches handle tab switching, but the Visibility API does not handle
// multiple open browser windows side by side.

/**
 * useSynchronizedPing - A React hook for chat synchronization based on window focus
 * and cross-tab communication.
 *
 * Features:
 * 1. Regular polling on 60-second interval when tab is visible
 * 2. Immediate update when tab becomes visible
 * 3. Throttling to prevent excessive server requests
 *
 * @returns {{ refresh: () => Promise<void> }} Object containing refresh function
 */
const useSynchronizedPing = ({
  onceInForeground = false,
  onInForeground,
}: {
  onceInForeground: boolean;
  onInForeground?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const hasRunForegroundPing = useRef(false);
  const { subscribe } = useFramePubSub();

  // Function to fetch data using Redux action
  const fetchData = useCallback(
    async (background: boolean) => {
      // NOTE(liamvdv): we want to deduplicate the foreground calls, thus refresh false if in foreground.
      const promise = dispatch(
        pingFrameWithCurrentState({
          refresh: background,
          background,
        })
      );
      if (!background) {
        await promise;
        onInForeground?.();
      }
    },
    [dispatch, onInForeground]
  );

  useEffect(() => {
    const handle = () => fetchData(true);

    const shouldPingInForeground =
      onceInForeground && !hasRunForegroundPing.current;

    // Initial fetch
    fetchData(!shouldPingInForeground);
    if (shouldPingInForeground) {
      hasRunForegroundPing.current = true;
    }

    // Subscribe to events
    window.addEventListener("focus", handle);
    const unsubscribe = subscribe("message_end", handle);
    const unsubscribe2 = subscribe("message_rating", handle);
    return () => {
      window.removeEventListener("focus", handle);
      unsubscribe();
      unsubscribe2();
    };
  }, [fetchData, onceInForeground]);

  return {
    refresh: fetchData,
  };
};

export default useSynchronizedPing;
